import { useState, useEffect, useRef } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { FileType, LogisticNetwork } from '@/types/data';
import Papa from 'papaparse';

type FileDataHookConfig<T> = {
  fileType: FileType;
  getEditedData: (networkId: string, fileId: string) => string | null;
  autosaveData: (
    currentNetwork: LogisticNetwork,
    data: T[],
    isEdited: boolean,
    setIsSaving: (isSaving: boolean) => void
  ) => boolean;
  createEmptyRow: () => T;
};

export const useFileData = <T>(
  currentNetwork: LogisticNetwork | null,
  config: FileDataHookConfig<T>
) => {
  const { setIsNetworkModified } = useNetworkContext();
  const [data, setData] = useState<T[]>([]);
  const [originalData, setOriginalData] = useState<T[]>([]);
  const [isEdited, setIsEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  // Load data from the selected network
  useEffect(() => {
    if (!currentNetwork) return;

    // Find the file in the network
    const file = currentNetwork.files.find((file) => file.type === config.fileType && file.content);

    if (file && file.content) {
      // Check if there's edited data in localStorage
      const editedData = config.getEditedData(currentNetwork.id, file.id);

      // Use edited data if available, otherwise use original content
      const contentToUse = editedData || file.content;

      // Parse CSV data
      const parsedData = Papa.parse<T>(contentToUse, {
        header: true,
        skipEmptyLines: true,
      });

      if (parsedData.data) {
        setData(parsedData.data);

        // If we're using edited data, set isEdited to true
        if (editedData) {
          // Original data should be from the file content
          const originalParsedData = Papa.parse<T>(file.content, {
            header: true,
            skipEmptyLines: true,
          });
          setOriginalData(originalParsedData.data || []);
          setIsEdited(true);
        } else {
          setOriginalData(parsedData.data);
          setIsEdited(false);
        }
      }
    }
  }, [currentNetwork, config.fileType]);

  // Autosave effect
  useEffect(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set a new timeout to autosave after 1 second of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      autosaveChanges();
    }, 1000);

    // Cleanup function
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [data]);

  // Autosave function
  const autosaveChanges = () => {
    if (!currentNetwork) return;

    // Use the provided autosave function
    config.autosaveData(currentNetwork, data, isEdited, setIsSaving);
  };

  // Function to handle row deletion
  const deleteRow = (rowIdx: number) => {
    const newRows = [...data];
    newRows.splice(rowIdx, 1);
    setData(newRows);
    setIsEdited(true);
    setIsNetworkModified(true);
  };

  // Handle row changes
  const handleRowsChange = (newRows: T[]) => {
    setData(newRows);
    setIsEdited(true);
    setIsNetworkModified(true);
  };

  // Add empty row to the table
  const addEmptyRow = () => {
    const emptyRow = config.createEmptyRow();
    setData([...data, emptyRow]);
    setIsEdited(true);
    setIsNetworkModified(true);
  };

  // Discard changes
  const discardChanges = () => {
    setData([...originalData]);
    setIsEdited(false);
    setIsNetworkModified(false);

    // Clear any pending autosave
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  };

  return {
    data,
    isEdited,
    isSaving,
    addEmptyRow,
    discardChanges,
    handleRowsChange,
    deleteRow,
  };
};
